import { makeAutoObservable } from "mobx";
import { Quantity } from "../core";
import { t, translate } from "../I18n";
import { PProductError } from "./PProductError";
export class PProductCardCreator {
    constructor(params) {
        this.ctx = params.ctx;
        this.domain = params.domain;
        this.productInstanceCreator = params.productInstanceCreator;
        this.fulfillmentTime = params.fulfillmentTime;
        this.openIntent = params.openIntent;
        this.crossSellingCreator = params.crossSellingCreator;
        this.fulfillmentInstructions = params.fulfillmentInstructions;
        makeAutoObservable(this);
    }
    get crossSellingAddToCartBottomSheet() {
        return this.ctx.crossSellingAddToCartBottomSheet;
    }
    get restaurantId() {
        return this.ctx.restaurantId;
    }
    get availability() {
        return this.ctx.getAvailability(this.domain);
    }
    get state() {
        return this.productInstanceCreator.state;
    }
    get depositIcon() {
        return this.product.depositIcon;
    }
    get price() {
        return this.ctx.getPrice(this.domain);
    }
    get priceValue() {
        return this.ctx.getPriceValue(this.domain);
    }
    get orderingHoursMessage() {
        if (this.domain.orderingHoursAvailability.isOutOfOrderingHours) {
            return PProductError.productCartOfOrderingHours(this.ctx.nextOrderAvailableAt, this.domain.orderingHoursAvailability.canPreorder);
        }
        return null;
    }
    // TODO: this should be in the domain
    get hasAnyAvailableFulfillmentTime() {
        return this.fulfillmentTime.hasAnyAvailableTime;
    }
    // TODO: this should be in the domain
    get notAvailableFulfillmentTimeOrEmpty() {
        return (!this.fulfillmentTime.hasValue ||
            !this.fulfillmentTime.isAvailableAtSelectedTime);
    }
    get noFulfillmentTime() {
        return !this.fulfillmentTime.hasValue;
    }
    get shouldChangeFulfillmentTime() {
        return (this.hasAnyAvailableFulfillmentTime &&
            this.notAvailableFulfillmentTimeOrEmpty);
    }
    get error() {
        return this.productInstanceCreator.error;
    }
    get quantity() {
        return this.domain.quantity.value;
    }
    get canDecrement() {
        return this.domain.quantity.gt(Quantity.One);
    }
    get canIncrement() {
        return true;
    }
    increment() {
        if (this.canIncrement) {
            this.domain.increment();
        }
    }
    decrement() {
        if (this.canDecrement) {
            this.domain.decrement();
        }
    }
    get labels() {
        return this.ctx.labels(this.productInstanceCreator.instance);
    }
    get product() {
        return this.productInstanceCreator.product;
    }
    get productTypeIdValues() {
        return this.domain.productTypeIds
            .rawValues()
            .map((productTypeId) => productTypeId.value);
    }
    get isSubmitVisible() {
        return (this.productInstanceCreator.state.isSubmitVisible &&
            (this.hasAnyAvailableFulfillmentTime || this.ctx.isIosReviewMode));
    }
    get isSubmitDisabled() {
        return !this.ctx.hasResult(this.domain);
    }
    finish() {
        // Run validations automatically on all fields and mark invalid as... invalid when trying to finish creator
        if (this.availability.isAvailable.isDefinitelyFalse) {
            this.productInstanceCreator.validate();
        }
        this.ctx.collectCreatorData(this.domain);
        return this.ctx.getResult(this.domain);
    }
    get isSplitPossible() {
        const { state, instance } = this.domain.productInstanceCreator;
        return this.ctx.isSplitPossible({
            isSplit: state.isSplit,
            halvingPolicy: state.productCategory.halvingPolicy,
            productInstance: instance,
            fulfillmentMethod: this.fulfillmentInstructions.fulfillmentMethod,
        });
    }
    get preorderSettings() {
        return this.ctx.getPreorderSettings(this.productInstanceCreator.instance);
    }
    get weeklySchedule() {
        return this.productInstanceCreator.instance.productWeeklySchedule;
    }
    split() {
        this.domain.split();
        this.productInstanceCreator.updateState();
    }
    removeFirstHalf() {
        this.domain.removeFirstHalf();
        this.productInstanceCreator.updateState();
    }
    removeSecondHalf() {
        this.domain.removeSecondHalf();
        this.productInstanceCreator.updateState();
    }
    setFirstHalfProductType(productTypeString) {
        const productType = this.ctx.getProductType(productTypeString);
        this.domain.setFirstHalfProductType(productType);
        this.productInstanceCreator.updateState();
    }
    setSecondHalfProductType(productTypeString) {
        const productType = this.ctx.getProductType(productTypeString);
        this.domain.setSecondHalfProductType(productType);
        this.productInstanceCreator.updateState();
    }
    get temporarilyUnavailableIngredients() {
        return this.productInstanceCreator.state.temporarilyUnavailableIngredients;
    }
    get submitLabel() {
        if (this.shouldChangeFulfillmentTime && this.noFulfillmentTime) {
            return translate(t.restaurants.widgets.menu.plan_date);
        }
        if (this.shouldChangeFulfillmentTime && !this.noFulfillmentTime) {
            return translate(t.restaurants.widgets.menu.change_date);
        }
        switch (this.openIntent) {
            case "add_from_cart":
            case "add_from_menu":
                return translate(t.theme_defaults.creator.add);
            case "edit_from_cart":
                return translate(t.theme_defaults.creator.update_cart);
        }
    }
}
